import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Link,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
} from '@apollo/orbit';
import { colors } from '@apollo/space-kit/colors';
import classnames from 'classnames';
import React, { useCallback, useMemo, useRef, useState } from 'react';

import { format } from 'src/lib/format';
import { DocsRouter } from 'src/lib/routers';

import { CursorContext } from './cursor/Cursor';
import { TraceNode } from './traceNode/TraceNode';
import { TraceTree } from './TraceTree';

const LegendItem = ({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) => (
  <div className="ml-4 inline-flex items-center pt-1 text-sm text-secondary">
    <div className={classnames('mr-2 size-3 rounded-sm', className)} />
    {children}
  </div>
);

const WaitingOnServerLegendItem = ({ traceTree }: { traceTree: TraceTree }) => {
  // If traces are reported from Gateway with high (>=10%) request-init latency
  const firstNode = traceTree.root.children[0];
  const requestStart = traceTree.requestStart || 0;
  const start = firstNode?.startTime || 0;
  const end = firstNode?.endTime || 0;
  const percentTimeWaitingOnServer =
    (100 * (start - requestStart)) / (end - start);
  const shouldShowRouterSuggestion =
    ['server', 'gateway'].some((agent) =>
      traceTree.agentVersion.includes(agent),
    ) && percentTimeWaitingOnServer > 10;

  if (!shouldShowRouterSuggestion)
    return (
      <LegendItem className="bg-blilet-lighter">waiting on server</LegendItem>
    );

  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <div className="inline-block">
          <LegendItem className="bg-blilet-lighter">
            waiting on server
            <IconInfo className="-mb-0.5 ml-1 size-4" />
          </LegendItem>
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-[224px]"
        data-testid="waiting-on-server-tooltip"
      >
        <PopoverBody>
          <div className="z-50 cursor-default text-sm">
            <p>
              High latency due to "waiting on server" is typically caused by
              processing too many concurrent requests with Apollo Gateway.
            </p>
            <p className="mt-2 font-semibold">
              Apollo Router is cheaper and easier to operate than Gateway,{' '}
              <Link
                to="https://www.apollographql.com/blog/announcement/backend/apollo-router-v1-12-improved-router-security-performance-and-extensibility/"
                isExternal
                className="text-wrap"
              >
                and can reduce latency by up to 70%.
              </Link>
            </p>
            <p className="mt-2">
              <Link to={DocsRouter.path('MigratingFromGateway')} isExternal>
                Visit the docs to learn more about Apollo Router and how to
                migrate.
              </Link>
            </p>
          </div>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const Trace = ({
  traceTree,
  rowHoverColor = colors.yellow.light,
  openNodes,
  onOpenChange,
}: {
  traceTree: TraceTree;
  rowHoverColor?: string;
  openNodes: Set<string>;
  onOpenChange: (nodeId: string) => void;
}) => {
  const [cursor, setCursor] = useState(-1); // number timestamp, start unset

  const [cursorStyle, setCursorStyle] = useState<{
    top: number;
    left: number;
  }>();

  const traceTimingRef = useRef<HTMLDivElement>(null);

  const isNodeOpen = useCallback(
    (nodeId: string) => openNodes.has(nodeId),
    [openNodes],
  );

  const erroredNodesPathIds = useMemo(
    () =>
      new Set(
        traceTree.erroredNodes.map((n) => n.path.map((p) => p.id)).flat(),
      ),
    [traceTree],
  );

  return (
    <>
      <div
        className="relative mb-4 grid grid-cols-[minmax(30%,min-content)_1fr_3rem_3.5rem] text-xs leading-normal"
        onMouseMove={(e) => {
          if (!traceTimingRef.current) return;
          const { x, width, height } =
            traceTimingRef.current.getBoundingClientRect();
          const pos = e.pageX - x;

          if (pos < 0 || pos > width) {
            setCursor(-1); // out of our trace box
            setCursorStyle(undefined);
          } else {
            setCursor(
              (traceTree.requestEnd - traceTree.requestStart) * (pos / width) +
                traceTree.requestStart,
            );
            setCursorStyle({
              left: e.pageX - e.currentTarget.getBoundingClientRect().x,
              top: height,
            });
          }
        }}
        onMouseLeave={() => {
          setCursor(-1);
          setCursorStyle(undefined);
        }}
      >
        <div
          className="z-10 border-b border-b-primary pb-2 text-sm font-semibold"
          style={{ gridColumn: 1 }}
        >
          Spans
        </div>

        <div
          ref={traceTimingRef}
          className="relative z-10 border-b border-b-primary pb-2 text-sm font-semibold"
          style={{ gridColumn: 2 }}
        >
          Timing
          {cursor > 0 && (
            <div
              className="absolute bottom-0 whitespace-nowrap font-normal leading-none text-secondary"
              style={{
                left: `${
                  (100 * (cursor - traceTree.requestStart)) /
                  (traceTree.requestEnd - traceTree.requestStart)
                }%`,
              }}
            >
              {format.ms(cursor - traceTree.requestStart)}
            </div>
          )}
        </div>
        <div
          className="z-10 border-b border-b-primary pb-2"
          style={{ gridColumn: 3 }}
        />
        <div
          className="z-10 border-b border-b-primary pb-2 text-center text-sm font-semibold"
          style={{ gridColumn: 4 }}
        >
          {traceTree.hasCachePolicy && 'TTL'}
        </div>

        <div
          className={classnames(
            'pointer-events-none absolute bottom-0 z-20 w-px bg-silver-darker',
            { hidden: !cursorStyle },
          )}
          style={cursorStyle}
        />
        <CursorContext.Provider value={cursor}>
          <TraceNode
            node={traceTree.root}
            requestStart={traceTree.requestStart}
            requestEnd={traceTree.requestEnd}
            criticalPath={traceTree.criticalPath}
            rowHoverColor={rowHoverColor}
            onOpenChange={onOpenChange}
            isOpen={isNodeOpen}
            erroredNodesPathIds={erroredNodesPathIds}
          />
        </CursorContext.Provider>
      </div>

      {traceTree.agentVersion.includes('router') ? (
        <div>
          {/* Router Color Legend */}
          <LegendItem className="bg-blilet-lighter">
            handling request / response
          </LegendItem>
          <LegendItem className="bg-blilet">processing request</LegendItem>
          <LegendItem className="bg-blilet-darker">
            <Link
              isExternal
              to="https://www.apollographql.com/docs/graphos/metrics/operations#deferred-fetches"
            >
              deferred fetch
            </Link>
          </LegendItem>
        </div>
      ) : (
        <div>
          {/* Legacy Color Legend */}
          <LegendItem className="border border-primary bg-secondary">
            child node
          </LegendItem>
          <WaitingOnServerLegendItem traceTree={traceTree} />
          <LegendItem className="bg-blilet">server responding</LegendItem>
          <LegendItem className="bg-blilet-darker">
            <Link
              isExternal
              to="https://www.apollographql.com/docs/graphos/metrics/operations#deferred-fetches"
            >
              deferred fetch
            </Link>
          </LegendItem>
        </div>
      )}
    </>
  );
};
